import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Search, Download, Loader2, ShieldCheck, Users,
  Plane, GraduationCap, Mail, Phone,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TRACKS } from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "univista_admin_token";

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success(`Welcome back, ${data.user.name}`);
      onLogin(data.access_token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 shadow-2xl"
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="font-display text-3xl font-semibold text-white">Studyvia</span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-primary border border-primary/40 rounded-full px-2 py-1">Admin</span>
        </div>
        <p className="text-white/50 text-sm mb-8 flex items-center gap-2">
          <ShieldCheck size={14} className="text-primary" /> Leads dashboard — authorised access only
        </p>

        <label className="text-xs text-white/70">Email</label>
        <Input
          type="email"
          data-testid="admin-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@univista.co.in"
          className="mt-1.5 mb-4 bg-white/90 border-white/20 h-11"
        />
        <label className="text-xs text-white/70">Password</label>
        <Input
          type="password"
          data-testid="admin-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1.5 mb-6 bg-white/90 border-white/20 h-11"
        />
        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {loading ? <><Loader2 className="animate-spin mr-2" size={18} /> Signing in...</> : "Sign In"}
        </Button>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon size={18} className="text-primary" />
      </div>
      <div className="font-display text-4xl font-semibold text-secondary mt-2">{value}</div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [track, setTrack] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchTrack = track === "all" || l.interest_track === track;
      const hay = `${l.name} ${l.email} ${l.contact_number} ${l.preferred_course} ${l.preferred_country || ""} ${l.program_level} ${l.partner || ""}`.toLowerCase();
      return matchTrack && hay.includes(q.toLowerCase());
    });
  }, [leads, q, track]);

  const stats = useMemo(() => ({
    total: leads.length,
    abroad: leads.filter((l) => l.interest_track === "Study Abroad").length,
    india: leads.filter((l) => (l.interest_track || "").startsWith("India")).length,
  }), [leads]);

  const exportCsv = () => {
    const cols = ["name", "contact_number", "email", "interest_track", "partner", "program_level", "education_qualification", "intake_year", "preferred_country", "preferred_course", "created_at"];
    const header = cols.join(",");
    const rows = filtered.map((l) =>
      cols.map((c) => `"${String(l[c] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `univista-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead(s)`);
  };

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl font-semibold">Studyvia</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-primary border border-primary/40 rounded-full px-2 py-1">Admin</span>
          </div>
          <Button
            variant="ghost"
            data-testid="admin-logout"
            onClick={onLogout}
            className="text-white/80 hover:text-white hover:bg-white/10 gap-2"
          >
            <LogOut size={16} /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display text-4xl text-secondary">Leads</h1>
            <p className="text-muted-foreground mt-1">Every enquiry captured from your website.</p>
          </div>
          <Button onClick={exportCsv} data-testid="export-csv" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            <Download size={16} /> Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Total Leads" value={stats.total} />
          <StatCard icon={Plane} label="Study Abroad" value={stats.abroad} />
          <StatCard icon={GraduationCap} label="India Education" value={stats.india} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="lead-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, course..."
              className="pl-9 h-11"
            />
          </div>
          <Select value={track} onValueChange={setTrack}>
            <SelectTrigger data-testid="track-filter" className="w-full sm:w-64 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tracks</SelectItem>
              {TRACKS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Contact</th>
                  <th className="px-4 py-3 font-medium">Track</th>
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Level</th>
                  <th className="px-4 py-3 font-medium">Course</th>
                  <th className="px-4 py-3 font-medium">Country</th>
                  <th className="px-4 py-3 font-medium">Intake</th>
                  <th className="px-4 py-3 font-medium">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-16 text-center text-muted-foreground">
                    <Loader2 className="animate-spin mx-auto" /> Loading leads...
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-16 text-center text-muted-foreground" data-testid="no-leads">
                    No leads found.
                  </td></tr>
                ) : filtered.map((l, i) => (
                  <tr key={l.id || i} className="hover:bg-muted/40 transition-colors" data-testid={`lead-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-secondary">{l.name}</td>
                    <td className="px-4 py-3 text-foreground/80">
                      <div className="flex items-center gap-1.5"><Mail size={12} className="text-primary" /> {l.email}</div>
                      <div className="flex items-center gap-1.5 text-muted-foreground"><Phone size={12} /> {l.contact_number}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[11px] rounded-full px-2.5 py-1 bg-primary/10 text-primary whitespace-nowrap">{l.interest_track || "—"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[11px] rounded-full px-2.5 py-1 bg-secondary/10 text-secondary whitespace-nowrap font-medium">{l.partner || "—"}</span>
                    </td>
                    <td className="px-4 py-3 text-foreground/80 whitespace-nowrap">{l.program_level || "—"}</td>
                    <td className="px-4 py-3 text-foreground/80">{l.preferred_course}</td>
                    <td className="px-4 py-3 text-foreground/80">{l.preferred_country || "—"}</td>
                    <td className="px-4 py-3 text-foreground/80">{l.intake_year}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); setChecking(false); });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success("Logged out");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
